import styled from 'styled-components';
import { v2Colors, v2Breakpoints, v2FontFamily } from '../../formInputsV2/styles/tokens';

// Scoped, additive restyle for the ANIA fast-quote confirmation modal in the
// autocarro (van) funnel. Started as a copy of the auto modal's styles and is
// kept separate on purpose, so the van modal can diverge without touching auto.
// Doesn't touch the shared `.x-modal`/`.general-modal-content`/`.custom-table`
// rules in App.css (used by other modals too) — every rule here is nested
// under this wrapper, so nothing outside the van fast-quote modal changes.
export const FastQuoteModalStyles = styled.div`
  font-family: ${v2FontFamily};

  .general-modal-content {
    padding: 22px 24px;
  }

  .modal-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid rgba(16, 24, 40, 0.08);
  }

  .modal-title {
    margin: 0;
    font-size: 16.5px;
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  .modal-header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  /* The close icon moves into the header row (out of App.css's absolute
     corner pin) so it lines up with the title and the Conferma button,
     and reads as a quiet muted control rather than a heavy black mark. */
  .general-modal-close {
    position: static;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    color: ${v2Colors.textMuted};
    transition: background-color 150ms ease, color 150ms ease;
  }

  .general-modal-close:hover {
    background-color: ${v2Colors.surface};
    color: ${v2Colors.text};
  }

  .general-modal-close svg {
    width: 82%;
    height: 82%;
    fill: currentColor;
  }

  /* Solid green, but compact — reads unmistakably as a button while staying
     clearly smaller than the funnel's Continua / Procedi. */
  && .modal-header button {
    min-height: 34px;
    padding: 7px 18px;
    font-size: 13px;
    border-radius: 8px;
    background-color: ${v2Colors.primary};
    border: 1.5px solid ${v2Colors.primary};
    color: #fff;
    box-shadow: 0 2px 6px rgba(0, 166, 81, 0.3);
  }

  && .modal-header button:hover {
    background-color: ${v2Colors.primaryHover};
    border-color: ${v2Colors.primaryHover};
    color: #fff;
    box-shadow: 0 4px 12px rgba(0, 166, 81, 0.4);
  }

  /* ANIA values are shown for review, not for editing — style them as filled
     read-only chips so they don't look like empty inputs to fill in. */
  .form-group {
    margin-bottom: 12px;

    label {
      display: block;
      margin-bottom: 5px;
      font-size: 13.5px;
      font-weight: 500;
      color: ${v2Colors.textMuted};
    }

    input.form-control {
      width: 100%;
      min-height: 42px;
      padding: 9px 12px;
      font-size: 15px;
      font-weight: 500;
      color: ${v2Colors.text};
      background-color: ${v2Colors.surface};
      border: 1px solid rgba(16, 24, 40, 0.06);
      border-radius: 8px;
      cursor: default;
    }

    input.form-control:focus {
      outline: none;
      box-shadow: none;
      border-color: rgba(16, 24, 40, 0.06);
    }
  }

  /* Soften Bootstrap's flat red alert into the v2 danger treatment. */
  .alert-danger {
    border-radius: 10px;
    border: 1px solid rgba(217, 45, 32, 0.3);
    background-color: ${v2Colors.dangerSoft};
    color: #b42318;
    font-size: 14px;
    line-height: 1.5;
    padding: 12px 14px;
  }

  .custom-table {
    display: block;
    overflow-x: auto;
    margin-top: 12px;
    font-size: 13.5px;
    border: 1px solid rgba(16, 24, 40, 0.1);
    border-radius: 10px;

    th,
    td {
      padding: 8px 11px;
      white-space: nowrap;
      text-align: left;
    }

    thead th {
      background: ${v2Colors.surface};
      font-weight: 600;
      color: ${v2Colors.textMuted};
    }

    tbody tr:nth-child(even) td {
      background: rgba(16, 24, 40, 0.02);
    }
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    .general-modal-content {
      padding: 18px 16px;
    }

    .modal-header {
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .modal-title {
      padding: 0 30px;
      font-size: 15px;
    }

    .form-group label {
      font-size: 12.5px;
    }

    .form-group input.form-control {
      font-size: 14px;
    }

    .alert-danger {
      font-size: 13px;
    }

    .custom-table {
      font-size: 12.5px;
    }

    .modal-header-actions {
      justify-content: center;
    }

    /* On small screens the close icon belongs in the modal corner on its
       own — not lined up next to the Conferma button. */
    .general-modal-close {
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 1;
    }
  }
`;
